<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { DirectUpload } from 'activestorage';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import InternalCommunicationAPI from 'dashboard/api/internalCommunication';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import MessageContent from './MessageContent.vue';
import InternalAttachmentList from './InternalAttachmentList.vue';
import ConversationReferenceCard from './ConversationReferenceCard.vue';

defineProps({
  essentialMode: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const selectedRoomId = ref(null);
const mobileRoomOpen = ref(false);
const messageList = ref(null);
const composer = ref(null);
const createGroupDialog = ref(null);
const draft = ref('');
const editMessageId = ref(null);
const pendingAttachments = ref([]);
const fileInput = ref(null);
const mentionableUsers = ref([]);
const selectedMentionUserIds = ref([]);
const showMentionSuggestions = ref(false);
const showReferenceSearch = ref(false);
const referenceQuery = ref('');
const referenceResults = ref([]);
const selectedReference = ref(null);
const isSearchingReferences = ref(false);
const isUploading = ref(false);
const groupName = ref('');
const groupDescription = ref('');
const groupUserIds = ref([]);
const manageMembers = ref(false);
const managedUserIds = ref([]);

const rooms = computed(
  () => store.getters['internalCommunication/getRooms'] || []
);
const selectedRoom = computed(() =>
  store.getters['internalCommunication/getRoom'](selectedRoomId.value)
);
const messages = computed(() =>
  store.getters['internalCommunication/getMessages'](selectedRoomId.value)
);
const hasMore = computed(() =>
  store.getters['internalCommunication/getHasMore'](selectedRoomId.value)
);
const uiFlags = computed(
  () => store.getters['internalCommunication/getUIFlags']
);
const currentUser = computed(() => store.getters.getCurrentUser || {});
const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const agents = computed(() => store.getters['agents/getAgents'] || []);
const isAdministrator = computed(
  () => currentAccount.value.role === 'administrator'
);
const teamRooms = computed(() =>
  rooms.value.filter(room => room.room_type === 'team')
);
const customRooms = computed(() =>
  rooms.value.filter(room => room.room_type === 'custom')
);
const currentMentionQuery = computed(() => {
  const match = draft.value.match(/@([^@\s]*)$/);
  return match?.[1]?.toLowerCase() || '';
});
const mentionSuggestions = computed(() => {
  if (!showMentionSuggestions.value) return [];
  return mentionableUsers.value
    .filter(user => user.name.toLowerCase().includes(currentMentionQuery.value))
    .slice(0, 6);
});
const activeMentionedUserIds = computed(() =>
  mentionableUsers.value
    .filter(
      user =>
        selectedMentionUserIds.value.includes(Number(user.id)) &&
        draft.value.includes(`@${user.name}`)
    )
    .map(user => user.id)
);
const canSend = computed(
  () =>
    selectedRoom.value?.can_post &&
    !uiFlags.value.isSending &&
    !isUploading.value &&
    (draft.value.trim() ||
      pendingAttachments.value.length ||
      selectedReference.value)
);

const conversationStatusLabels = computed(() => ({
  open: t('INTERNAL_COMMUNICATION.REFERENCE.STATUS.OPEN'),
  resolved: t('INTERNAL_COMMUNICATION.REFERENCE.STATUS.RESOLVED'),
  pending: t('INTERNAL_COMMUNICATION.REFERENCE.STATUS.PENDING'),
  snoozed: t('INTERNAL_COMMUNICATION.REFERENCE.STATUS.SNOOZED'),
}));

const formatConversationStatus = status => {
  return conversationStatusLabels.value[status] || status;
};

const parseError = error =>
  error?.response?.data?.error ||
  error?.message ||
  t('INTERNAL_COMMUNICATION.ERRORS.LOAD');

const scrollToBottom = async () => {
  await nextTick();
  if (messageList.value) {
    messageList.value.scrollTop = messageList.value.scrollHeight;
  }
};

const fetchMentionableUsers = async roomId => {
  const { data } = await InternalCommunicationAPI.getMentionableUsers(roomId);
  mentionableUsers.value = data;
};

const loadRoom = async room => {
  try {
    selectedRoomId.value = room.id;
    mobileRoomOpen.value = true;
    editMessageId.value = null;
    draft.value = '';
    selectedMentionUserIds.value = [];
    pendingAttachments.value = [];
    selectedReference.value = null;
    manageMembers.value = false;
    const [detailedRoom] = await Promise.all([
      store.dispatch('internalCommunication/fetchRoom', room.id),
      store.dispatch('internalCommunication/fetchMessages', {
        roomId: room.id,
      }),
      store.dispatch('internalCommunication/markRead', room.id),
      fetchMentionableUsers(room.id),
    ]);
    managedUserIds.value = (detailedRoom.members || []).map(member =>
      Number(member.id)
    );
    await scrollToBottom();
  } catch (error) {
    await store.dispatch('internalCommunication/fetchRooms');
    selectedRoomId.value = null;
    mobileRoomOpen.value = false;
    useAlert(parseError(error));
  }
};

const loadEarlierMessages = async () => {
  const firstSequence = messages.value[0]?.sequence;
  if (!firstSequence) return;
  await store.dispatch('internalCommunication/fetchMessages', {
    roomId: selectedRoomId.value,
    beforeSequence: firstSequence,
    prepend: true,
  });
};

const selectMention = user => {
  draft.value = draft.value.replace(/@([^@\s]*)$/, `@${user.name} `);
  selectedMentionUserIds.value = [
    ...new Set([...selectedMentionUserIds.value, Number(user.id)]),
  ];
  showMentionSuggestions.value = false;
};

const handleDraftInput = () => {
  showMentionSuggestions.value = /@([^@\s]*)$/.test(draft.value);
};

const createClientId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const requestModerationReason = () => {
  // eslint-disable-next-line no-alert
  return window.prompt(t('INTERNAL_COMMUNICATION.MESSAGES.MODERATION_REASON'));
};

const sendMessage = async () => {
  if (!canSend.value) return;
  try {
    await store.dispatch('internalCommunication/sendMessage', {
      roomId: selectedRoomId.value,
      message: {
        content: draft.value,
        client_id: createClientId(),
        attachment_ids: pendingAttachments.value.map(item => item.id),
        mentioned_user_ids: activeMentionedUserIds.value,
        conversation_display_id: selectedReference.value?.display_id,
      },
    });
    draft.value = '';
    selectedMentionUserIds.value = [];
    pendingAttachments.value = [];
    selectedReference.value = null;
    showReferenceSearch.value = false;
    await store.dispatch(
      'internalCommunication/markRead',
      selectedRoomId.value
    );
    await scrollToBottom();
  } catch (error) {
    useAlert(parseError(error));
  }
};

const startEdit = message => {
  editMessageId.value = message.id;
  draft.value = message.content || '';
  selectedMentionUserIds.value = (message.mentioned_user_ids || []).map(Number);
  pendingAttachments.value = [];
  selectedReference.value = null;
  nextTick(() => composer.value?.focus());
};

const cancelEdit = () => {
  editMessageId.value = null;
  draft.value = '';
  selectedMentionUserIds.value = [];
};

const saveEdit = async () => {
  const message = messages.value.find(item => item.id === editMessageId.value);
  if (!message) return;
  const needsReason =
    message.author?.id !== currentUser.value.id ||
    Date.now() - new Date(message.created_at).getTime() > 15 * 60 * 1000;
  const reason = needsReason ? requestModerationReason() : null;
  if (needsReason && !reason) return;
  try {
    await store.dispatch('internalCommunication/updateMessage', {
      roomId: selectedRoomId.value,
      messageId: message.id,
      message: {
        content: draft.value,
        mentioned_user_ids: activeMentionedUserIds.value,
        reason,
      },
    });
    cancelEdit();
  } catch (error) {
    useAlert(parseError(error));
  }
};

const deleteMessage = async message => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INTERNAL_COMMUNICATION.MESSAGES.CONFIRM_DELETE'))) {
    return;
  }
  const needsReason =
    message.author?.id !== currentUser.value.id ||
    Date.now() - new Date(message.created_at).getTime() > 15 * 60 * 1000;
  const reason = needsReason ? requestModerationReason() : null;
  if (needsReason && !reason) return;
  try {
    await store.dispatch('internalCommunication/deleteMessage', {
      roomId: selectedRoomId.value,
      messageId: message.id,
      reason,
    });
  } catch (error) {
    useAlert(parseError(error));
  }
};

const uploadFile = file =>
  new Promise((resolve, reject) => {
    const upload = new DirectUpload(
      file,
      `/api/v1/accounts/${route.params.accountId}/internal_communication/rooms/${selectedRoomId.value}/direct_uploads`,
      {
        directUploadWillCreateBlobWithXHR: xhr => {
          xhr.setRequestHeader(
            'api_access_token',
            currentUser.value.access_token
          );
        },
      }
    );
    upload.create((error, blob) => {
      if (error) reject(error);
      else resolve({ id: blob.attachment_id, filename: file.name });
    });
  });

const handleFiles = async event => {
  const files = Array.from(event.target.files || []);
  event.target.value = '';
  if (files.length + pendingAttachments.value.length > 5) {
    useAlert(t('INTERNAL_COMMUNICATION.COMPOSER.FILES_LIMIT'));
    return;
  }
  isUploading.value = true;
  try {
    const uploaded = await Promise.all(files.map(uploadFile));
    pendingAttachments.value.push(...uploaded);
  } catch (error) {
    useAlert(parseError(error));
  } finally {
    isUploading.value = false;
  }
};

const removePendingAttachment = attachmentId => {
  pendingAttachments.value = pendingAttachments.value.filter(
    item => item.id !== attachmentId
  );
};

const searchReferences = async () => {
  if (!referenceQuery.value.trim()) return;
  isSearchingReferences.value = true;
  try {
    const { data } = await InternalCommunicationAPI.searchConversations(
      referenceQuery.value
    );
    referenceResults.value = data;
  } catch (error) {
    useAlert(parseError(error));
  } finally {
    isSearchingReferences.value = false;
  }
};

const chooseReference = reference => {
  selectedReference.value = reference;
  showReferenceSearch.value = false;
  referenceResults.value = [];
  referenceQuery.value = '';
};

const resetGroupForm = () => {
  groupName.value = '';
  groupDescription.value = '';
  groupUserIds.value = [];
};

const openCreateGroup = () => {
  createGroupDialog.value?.open();
};

const createGroup = async () => {
  if (!groupName.value.trim()) return;
  try {
    const room = await store.dispatch('internalCommunication/createRoom', {
      name: groupName.value,
      description: groupDescription.value,
      user_ids: groupUserIds.value,
    });
    createGroupDialog.value?.close();
    resetGroupForm();
    useAlert(t('INTERNAL_COMMUNICATION.GROUP.CREATED'));
    await loadRoom(room);
  } catch (error) {
    useAlert(parseError(error));
  }
};

const saveMembers = async () => {
  try {
    await store.dispatch('internalCommunication/replaceMembers', {
      roomId: selectedRoomId.value,
      userIds: managedUserIds.value,
    });
    manageMembers.value = false;
  } catch (error) {
    useAlert(parseError(error));
  }
};

const leaveRoom = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INTERNAL_COMMUNICATION.MEMBERS.CONFIRM_LEAVE'))) {
    return;
  }
  try {
    await store.dispatch(
      'internalCommunication/leaveRoom',
      selectedRoomId.value
    );
    selectedRoomId.value = null;
    mobileRoomOpen.value = false;
  } catch (error) {
    useAlert(parseError(error));
  }
};

const archiveRoom = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INTERNAL_COMMUNICATION.MEMBERS.CONFIRM_ARCHIVE'))) {
    return;
  }
  try {
    await store.dispatch(
      'internalCommunication/archiveRoom',
      selectedRoomId.value
    );
    selectedRoomId.value = null;
    mobileRoomOpen.value = false;
  } catch (error) {
    useAlert(parseError(error));
  }
};

const goBackToEssential = () => {
  router.push(frontendURL(`accounts/${route.params.accountId}/essential`));
};

const formatTime = value =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : '';

watch(
  () => selectedRoom.value?.members,
  members => {
    if (!manageMembers.value) {
      managedUserIds.value = (members || []).map(member => Number(member.id));
    }
  }
);

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('internalCommunication/fetchRooms'),
      store.dispatch('agents/get'),
    ]);
    const requestedRoom = rooms.value.find(
      room => Number(room.id) === Number(route.query.room)
    );
    const firstRoom = requestedRoom || rooms.value[0];
    if (firstRoom) await loadRoom(firstRoom);
  } catch (error) {
    useAlert(parseError(error));
  }
});

onBeforeUnmount(() => {
  store.commit('internalCommunication/SET_ACTIVE_ROOM', null);
});
</script>

<template>
  <section
    class="flex h-full w-full min-w-0 overflow-hidden bg-n-surface-1 text-n-slate-12"
  >
    <aside
      class="flex h-full w-full flex-col border-r border-n-weak bg-n-surface-1 md:w-72 md:flex-shrink-0"
      :class="{ 'hidden md:flex': mobileRoomOpen }"
    >
      <header class="grid gap-3 border-b border-n-weak p-4">
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <h1 class="m-0 truncate text-lg font-semibold">
              {{ t('INTERNAL_COMMUNICATION.HEADER.TITLE') }}
            </h1>
            <p class="m-0 truncate text-xs text-n-slate-11">
              {{ t('INTERNAL_COMMUNICATION.HEADER.SUBTITLE') }}
            </p>
          </div>
          <Button
            v-if="isAdministrator"
            icon="i-lucide-plus"
            size="sm"
            variant="ghost"
            color="slate"
            :title="t('INTERNAL_COMMUNICATION.ROOMS.NEW_GROUP')"
            @click="openCreateGroup"
          />
        </div>
        <Button
          v-if="essentialMode"
          icon="i-lucide-arrow-left"
          size="xs"
          variant="link"
          @click="goBackToEssential"
        >
          {{ t('INTERNAL_COMMUNICATION.NAVIGATION.BACK_TO_ESSENTIAL') }}
        </Button>
      </header>

      <div
        v-if="uiFlags.isFetchingRooms"
        class="flex flex-1 items-center justify-center"
      >
        <Spinner />
      </div>
      <div v-else class="flex-1 overflow-y-auto p-2">
        <template v-if="rooms.length">
          <div v-if="teamRooms.length" class="mb-4">
            <p
              class="m-0 px-2 py-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('INTERNAL_COMMUNICATION.ROOMS.TEAM_SECTION') }}
            </p>
            <button
              v-for="room in teamRooms"
              :key="room.id"
              type="button"
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-all duration-100 ease-out hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 focus-visible:outline-none active:scale-[0.99]"
              :class="{
                'bg-n-alpha-2 text-n-blue-text': selectedRoomId === room.id,
              }"
              @click="loadRoom(room)"
            >
              <i class="i-lucide-users size-4 text-n-slate-11" />
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-medium">{{
                  room.name
                }}</span>
                <span class="block truncate text-xs text-n-slate-11">
                  {{
                    room.last_message?.content ||
                    t('INTERNAL_COMMUNICATION.ROOMS.NO_MESSAGES')
                  }}
                </span>
              </span>
              <span
                v-if="room.unread_count"
                class="flex min-w-5 items-center justify-center rounded-full bg-n-brand px-1 text-xs font-semibold text-white"
              >
                {{
                  room.unread_count > 99
                    ? t('INTERNAL_COMMUNICATION.NAVIGATION.BADGE_OVERFLOW')
                    : room.unread_count
                }}
              </span>
            </button>
          </div>

          <div v-if="customRooms.length">
            <p
              class="m-0 px-2 py-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('INTERNAL_COMMUNICATION.ROOMS.GROUP_SECTION') }}
            </p>
            <button
              v-for="room in customRooms"
              :key="room.id"
              type="button"
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-all duration-100 ease-out hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 focus-visible:outline-none active:scale-[0.99]"
              :class="{
                'bg-n-alpha-2 text-n-blue-text': selectedRoomId === room.id,
              }"
              @click="loadRoom(room)"
            >
              <i class="i-lucide-messages-square size-4 text-n-slate-11" />
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-medium">{{
                  room.name
                }}</span>
                <span class="block truncate text-xs text-n-slate-11">
                  {{
                    room.last_message?.content ||
                    t('INTERNAL_COMMUNICATION.ROOMS.NO_MESSAGES')
                  }}
                </span>
              </span>
              <span
                v-if="room.unread_count"
                class="flex min-w-5 items-center justify-center rounded-full bg-n-brand px-1 text-xs font-semibold text-white"
              >
                {{
                  room.unread_count > 99
                    ? t('INTERNAL_COMMUNICATION.NAVIGATION.BADGE_OVERFLOW')
                    : room.unread_count
                }}
              </span>
            </button>
          </div>
        </template>
        <p v-else class="m-0 p-6 text-center text-sm text-n-slate-11">
          {{ t('INTERNAL_COMMUNICATION.ROOMS.EMPTY') }}
        </p>
      </div>
    </aside>

    <main
      class="flex min-w-0 flex-1 flex-col"
      :class="{ 'hidden md:flex': !mobileRoomOpen && !selectedRoom }"
    >
      <template v-if="selectedRoom">
        <header
          class="flex min-h-16 items-center gap-3 border-b border-n-weak px-4 py-3"
        >
          <Button
            type="button"
            icon="i-lucide-arrow-left"
            size="sm"
            variant="ghost"
            color="slate"
            class="md:hidden"
            @click="mobileRoomOpen = false"
          />
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <h2 class="m-0 truncate text-base font-semibold">
                {{ selectedRoom.name }}
              </h2>
              <span
                v-if="selectedRoom.archived"
                class="rounded bg-n-slate-3 px-1.5 py-0.5 text-xs text-n-slate-11"
              >
                {{ t('INTERNAL_COMMUNICATION.ROOMS.ARCHIVED') }}
              </span>
            </div>
            <p class="m-0 truncate text-xs text-n-slate-11">
              {{ selectedRoom.description }}
            </p>
          </div>
          <Button
            type="button"
            icon="i-lucide-panel-right"
            size="sm"
            variant="ghost"
            color="slate"
            class="lg:hidden"
            @click="manageMembers = !manageMembers"
          />
        </header>

        <div
          class="flex items-center gap-2 border-b border-n-weak bg-n-amber-2 px-4 py-2 text-xs text-n-amber-11"
        >
          <i class="i-lucide-shield-check size-4" />
          <span>{{ t('INTERNAL_COMMUNICATION.HEADER.ADMIN_NOTICE') }}</span>
        </div>

        <div ref="messageList" class="flex-1 overflow-y-auto px-4 py-5">
          <div class="mx-auto grid max-w-3xl gap-4">
            <Button
              v-if="hasMore"
              size="xs"
              variant="link"
              class="justify-self-center"
              @click="loadEarlierMessages"
            >
              {{ t('INTERNAL_COMMUNICATION.MESSAGES.LOAD_MORE') }}
            </Button>

            <div
              v-if="!messages.length && !uiFlags.isFetchingMessages"
              class="grid justify-items-center gap-2 py-16 text-center"
            >
              <span
                class="flex size-12 items-center justify-center rounded-2xl bg-n-slate-3"
              >
                <i class="i-lucide-messages-square size-6 text-n-slate-11" />
              </span>
              <h3 class="m-0 text-base font-semibold">
                {{ t('INTERNAL_COMMUNICATION.MESSAGES.EMPTY_TITLE') }}
              </h3>
              <p class="m-0 max-w-md text-sm text-n-slate-11">
                {{ t('INTERNAL_COMMUNICATION.MESSAGES.EMPTY_DESC') }}
              </p>
            </div>

            <article
              v-for="message in messages"
              :key="message.id"
              class="group -mx-2 flex gap-3 rounded-lg px-2 py-1.5 transition-colors duration-150 hover:bg-n-alpha-1 focus-within:bg-n-alpha-1"
              :class="{
                'opacity-60': message.optimistic,
                'bg-n-alpha-1': editMessageId === message.id,
              }"
            >
              <img
                v-if="message.author?.avatar_url"
                :src="message.author.avatar_url"
                :alt="message.author.name"
                class="size-8 flex-shrink-0 rounded-full object-cover"
              />
              <span
                v-else
                class="flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-n-slate-3"
              >
                <i class="i-lucide-user size-4 text-n-slate-11" />
              </span>
              <div class="min-w-0 flex-1">
                <div class="mb-1 flex flex-wrap items-center gap-2">
                  <span class="text-sm font-semibold">
                    {{ message.author?.name || currentUser.name }}
                  </span>
                  <time class="text-xs text-n-slate-10">
                    {{ formatTime(message.created_at) }}
                  </time>
                  <span v-if="message.edited" class="text-xs text-n-slate-10">
                    {{ t('INTERNAL_COMMUNICATION.MESSAGES.EDITED') }}
                  </span>
                  <span
                    v-if="message.optimistic"
                    class="text-xs text-n-slate-10"
                  >
                    {{ t('INTERNAL_COMMUNICATION.MESSAGES.PENDING') }}
                  </span>
                </div>
                <div
                  v-if="message.deleted"
                  class="flex items-center gap-2 text-sm italic text-n-slate-10"
                >
                  <i class="i-lucide-trash-2 size-4" />
                  {{ t('INTERNAL_COMMUNICATION.MESSAGES.DELETED') }}
                </div>
                <template v-else>
                  <MessageContent
                    v-if="message.content"
                    :content="message.content"
                  />
                  <InternalAttachmentList :attachments="message.attachments" />
                  <ConversationReferenceCard
                    v-if="message.conversation_reference_id"
                    :reference-id="message.conversation_reference_id"
                    :account-id="route.params.accountId"
                    :essential-mode="essentialMode"
                  />
                </template>
                <div
                  v-if="
                    !message.deleted && (message.can_edit || message.can_delete)
                  "
                  class="mt-1 flex gap-3 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                >
                  <Button
                    v-if="message.can_edit"
                    size="xs"
                    variant="link"
                    color="slate"
                    @click="startEdit(message)"
                  >
                    {{ t('INTERNAL_COMMUNICATION.MESSAGES.EDIT') }}
                  </Button>
                  <Button
                    v-if="message.can_delete"
                    size="xs"
                    variant="link"
                    color="ruby"
                    @click="deleteMessage(message)"
                  >
                    {{ t('INTERNAL_COMMUNICATION.MESSAGES.DELETE') }}
                  </Button>
                </div>
              </div>
            </article>
          </div>
        </div>

        <footer v-if="selectedRoom.can_post" class="border-t border-n-weak p-3">
          <div
            class="relative mx-auto max-w-3xl rounded-xl border border-n-weak bg-n-surface-1 p-3 transition-colors duration-200 hover:border-n-slate-6 focus-within:border-n-brand"
            :class="{ 'border-n-brand bg-n-blue-2/30': editMessageId }"
          >
            <div
              v-if="editMessageId"
              class="mb-2 flex items-center gap-2 rounded-lg bg-n-blue-2 px-2 py-1.5 text-xs font-medium text-n-blue-11"
            >
              <i class="i-lucide-pencil size-3.5" />
              <span>{{ t('INTERNAL_COMMUNICATION.MESSAGES.EDITING') }}</span>
            </div>

            <div
              v-if="mentionSuggestions.length"
              class="absolute bottom-full left-3 z-20 mb-2 w-64 rounded-lg border border-n-weak bg-n-solid-2 p-1 shadow-lg"
            >
              <button
                v-for="user in mentionSuggestions"
                :key="user.id"
                type="button"
                class="flex h-8 w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-all duration-200 ease-in-out hover:bg-n-alpha-1 focus-visible:bg-n-alpha-1 focus-visible:outline-none dark:hover:bg-n-alpha-2 dark:focus-visible:bg-n-alpha-2"
                @click="selectMention(user)"
              >
                <i class="i-lucide-at-sign size-4 text-n-slate-11" />
                <span class="truncate">{{ user.name }}</span>
              </button>
            </div>

            <textarea
              ref="composer"
              v-model="draft"
              rows="3"
              class="w-full resize-none border-0 bg-transparent p-0 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10 focus:ring-0"
              :placeholder="t('INTERNAL_COMMUNICATION.COMPOSER.PLACEHOLDER')"
              @input="handleDraftInput"
              @keydown.ctrl.enter.prevent="
                editMessageId ? saveEdit() : sendMessage()
              "
              @keydown.meta.enter.prevent="
                editMessageId ? saveEdit() : sendMessage()
              "
            />

            <div
              v-if="pendingAttachments.length"
              class="flex flex-wrap gap-2 py-2"
            >
              <span
                v-for="attachment in pendingAttachments"
                :key="attachment.id"
                class="flex items-center gap-2 rounded-lg bg-n-slate-3 px-2 py-1 text-xs"
              >
                <i class="i-lucide-paperclip size-3.5" />
                <span class="max-w-48 truncate">{{ attachment.filename }}</span>
                <button
                  type="button"
                  class="flex size-5 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 focus-visible:outline-none"
                  :title="
                    t('INTERNAL_COMMUNICATION.COMPOSER.REMOVE_ATTACHMENT')
                  "
                  @click="removePendingAttachment(attachment.id)"
                >
                  <i class="i-lucide-x size-3.5" />
                </button>
              </span>
            </div>

            <div
              v-if="selectedReference"
              class="mb-2 flex items-center justify-between rounded-lg bg-n-blue-2 p-2 text-sm"
            >
              <span class="truncate">
                {{
                  t('INTERNAL_COMMUNICATION.REFERENCE.SELECTED', {
                    id: selectedReference.display_id,
                    name: selectedReference.contact.name,
                  })
                }}
              </span>
              <button
                type="button"
                class="flex size-6 flex-shrink-0 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 focus-visible:outline-none"
                :title="t('INTERNAL_COMMUNICATION.COMPOSER.REMOVE_REFERENCE')"
                @click="selectedReference = null"
              >
                <i class="i-lucide-x size-4" />
              </button>
            </div>

            <div
              v-if="showReferenceSearch"
              class="mb-2 grid gap-2 rounded-lg border border-n-weak p-2"
            >
              <div class="flex gap-2">
                <Input
                  v-model="referenceQuery"
                  type="search"
                  size="sm"
                  class="min-w-0 flex-1"
                  :placeholder="
                    t('INTERNAL_COMMUNICATION.COMPOSER.REFERENCE_PLACEHOLDER')
                  "
                  @enter="searchReferences"
                />
                <Button
                  size="sm"
                  variant="faded"
                  color="slate"
                  :is-loading="isSearchingReferences"
                  @click="searchReferences"
                >
                  {{ t('INTERNAL_COMMUNICATION.COMPOSER.SEARCH') }}
                </Button>
              </div>
              <div
                v-if="referenceResults.length"
                class="max-h-48 overflow-y-auto"
              >
                <button
                  v-for="reference in referenceResults"
                  :key="reference.display_id"
                  type="button"
                  class="flex w-full items-center justify-between gap-3 rounded-lg p-2 text-left transition-all duration-200 ease-in-out hover:bg-n-alpha-1 focus-visible:bg-n-alpha-1 focus-visible:outline-none dark:hover:bg-n-alpha-2 dark:focus-visible:bg-n-alpha-2"
                  @click="chooseReference(reference)"
                >
                  <span class="min-w-0">
                    <span class="block truncate text-sm font-medium">
                      {{ reference.contact.name }}
                    </span>
                    <span class="block text-xs text-n-slate-11">
                      {{
                        t('INTERNAL_COMMUNICATION.REFERENCE.SEARCH_RESULT', {
                          id: reference.display_id,
                          inbox: reference.inbox.name,
                        })
                      }}
                    </span>
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ formatConversationStatus(reference.status) }}
                  </span>
                </button>
              </div>
              <p
                v-else-if="referenceQuery && !isSearchingReferences"
                class="m-0 p-2 text-xs text-n-slate-11"
              >
                {{ t('INTERNAL_COMMUNICATION.COMPOSER.NO_REFERENCE_RESULTS') }}
              </p>
            </div>

            <div
              class="flex items-center justify-between gap-3 border-t border-n-weak pt-2"
            >
              <div class="flex items-center gap-1">
                <input
                  ref="fileInput"
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.webp,.pdf,.txt,.csv,.docx,.xlsx,.pptx"
                  class="hidden"
                  @change="handleFiles"
                />
                <Button
                  icon="i-lucide-paperclip"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  :is-loading="isUploading"
                  :disabled="isUploading"
                  :title="t('INTERNAL_COMMUNICATION.COMPOSER.ATTACH')"
                  @click="fileInput.click()"
                />
                <Button
                  icon="i-lucide-message-square-share"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  :class="{ '!bg-n-alpha-2': showReferenceSearch }"
                  :title="t('INTERNAL_COMMUNICATION.COMPOSER.REFERENCE')"
                  @click="showReferenceSearch = !showReferenceSearch"
                />
              </div>
              <div class="flex items-center gap-2">
                <Button
                  v-if="editMessageId"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  @click="cancelEdit"
                >
                  {{ t('INTERNAL_COMMUNICATION.MESSAGES.CANCEL') }}
                </Button>
                <Button
                  size="sm"
                  :disabled="editMessageId ? !draft.trim() : !canSend"
                  :is-loading="uiFlags.isSending"
                  @click="editMessageId ? saveEdit() : sendMessage()"
                >
                  {{
                    editMessageId
                      ? t('INTERNAL_COMMUNICATION.MESSAGES.SAVE')
                      : t('INTERNAL_COMMUNICATION.COMPOSER.SEND')
                  }}
                </Button>
              </div>
            </div>
          </div>
        </footer>
      </template>
    </main>

    <aside
      v-if="selectedRoom"
      class="absolute inset-y-0 right-0 z-30 flex w-80 flex-col border-l border-n-weak bg-n-surface-1 shadow-lg lg:static lg:z-auto lg:w-72 lg:shadow-none"
      :class="{ 'hidden lg:flex': !manageMembers }"
    >
      <header
        class="flex min-h-16 items-center justify-between border-b border-n-weak px-4"
      >
        <h3 class="m-0 text-sm font-semibold">
          {{ t('INTERNAL_COMMUNICATION.MEMBERS.TITLE') }}
        </h3>
        <Button
          type="button"
          icon="i-lucide-x"
          size="sm"
          variant="ghost"
          color="slate"
          class="lg:hidden"
          @click="manageMembers = false"
        />
      </header>
      <div class="flex-1 overflow-y-auto p-3">
        <label
          v-for="agent in agents"
          :key="agent.id"
          class="flex items-center gap-3 rounded-lg p-2"
          :class="{
            'hover:bg-n-alpha-2': selectedRoom.can_manage,
            'opacity-50':
              !selectedRoom.can_manage &&
              !(selectedRoom.members || []).some(
                member => member.id === agent.id
              ),
          }"
        >
          <input
            v-if="selectedRoom.can_manage"
            v-model="managedUserIds"
            type="checkbox"
            :value="Number(agent.id)"
          />
          <span
            v-else-if="
              (selectedRoom.members || []).some(
                member => member.id === agent.id
              )
            "
            class="i-lucide-user-check size-4 text-n-teal-10"
          />
          <span class="min-w-0 flex-1 truncate text-sm">{{ agent.name }}</span>
        </label>
      </div>
      <footer class="grid gap-2 border-t border-n-weak p-3">
        <Button
          v-if="selectedRoom.can_manage"
          size="sm"
          class="w-full"
          @click="saveMembers"
        >
          {{ t('INTERNAL_COMMUNICATION.MEMBERS.SAVE') }}
        </Button>
        <Button
          v-if="selectedRoom.can_leave"
          size="sm"
          variant="outline"
          color="slate"
          class="w-full"
          @click="leaveRoom"
        >
          {{ t('INTERNAL_COMMUNICATION.MEMBERS.LEAVE') }}
        </Button>
        <Button
          v-if="selectedRoom.can_manage && !selectedRoom.archived"
          size="sm"
          variant="ghost"
          color="ruby"
          class="w-full"
          @click="archiveRoom"
        >
          {{ t('INTERNAL_COMMUNICATION.MEMBERS.ARCHIVE') }}
        </Button>
      </footer>
    </aside>

    <Dialog
      ref="createGroupDialog"
      :title="t('INTERNAL_COMMUNICATION.GROUP.CREATE_TITLE')"
      :cancel-button-label="t('INTERNAL_COMMUNICATION.GROUP.CANCEL')"
      :confirm-button-label="t('INTERNAL_COMMUNICATION.GROUP.CREATE')"
      :disable-confirm-button="!groupName.trim()"
      overflow-y-auto
      @close="resetGroupForm"
      @confirm="createGroup"
    >
      <div class="grid max-h-[65vh] gap-4 overflow-y-auto px-0.5">
        <Input
          v-model="groupName"
          required
          maxlength="100"
          :label="t('INTERNAL_COMMUNICATION.GROUP.NAME')"
          :placeholder="t('INTERNAL_COMMUNICATION.GROUP.NAME_PLACEHOLDER')"
        />
        <TextArea
          v-model="groupDescription"
          :label="t('INTERNAL_COMMUNICATION.GROUP.DESCRIPTION')"
          :placeholder="
            t('INTERNAL_COMMUNICATION.GROUP.DESCRIPTION_PLACEHOLDER')
          "
        />
        <fieldset class="grid gap-1">
          <legend class="mb-2 text-sm font-medium">
            {{ t('INTERNAL_COMMUNICATION.GROUP.MEMBERS') }}
          </legend>
          <label
            v-for="agent in agents"
            :key="agent.id"
            class="flex items-center gap-3 rounded-lg p-2 hover:bg-n-alpha-2"
          >
            <input
              v-model="groupUserIds"
              type="checkbox"
              :value="Number(agent.id)"
            />
            <span class="truncate text-sm">{{ agent.name }}</span>
          </label>
        </fieldset>
      </div>
    </Dialog>
  </section>
</template>
